/**
 * Thin fetch wrapper for the Django session-auth API.
 *
 * Responsibilities:
 *   - Send the session cookie on every request (credentials: 'include').
 *   - For unsafe methods (POST/PUT/PATCH/DELETE), read the csrftoken cookie
 *     fresh and echo it in the X-CSRFToken header. Reading it per-request
 *     matters because Django rotates the token on login.
 *   - Normalise errors into an ApiError carrying the server's message.
 */

const API_BASE = (process.env.REACT_APP_API_BASE || 'http://localhost:8000').replace(/\/$/, '');

/** Prefix a relative media path (e.g. a Product's portrait_url) with the API origin. */
export const mediaUrl = (path) => (path ? `${API_BASE}${path}` : '');

const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS', 'TRACE']);

export class ApiError extends Error {
  constructor(message, status, data) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
  }
}

/** Read a cookie value by name from document.cookie. */
function getCookie(name) {
  const match = document.cookie.match(new RegExp('(^|;\\s*)' + name + '=([^;]*)'));
  return match ? decodeURIComponent(match[2]) : null;
}

async function request(path, { method = 'GET', body, headers = {} } = {}) {
  const upperMethod = method.toUpperCase();
  const finalHeaders = { Accept: 'application/json', ...headers };

  if (body !== undefined) {
    finalHeaders['Content-Type'] = 'application/json';
  }
  if (!SAFE_METHODS.has(upperMethod)) {
    const csrfToken = getCookie('csrftoken');
    if (csrfToken) finalHeaders['X-CSRFToken'] = csrfToken;
  }

  let response;
  try {
    response = await fetch(`${API_BASE}${path}`, {
      method: upperMethod,
      credentials: 'include',
      headers: finalHeaders,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch (networkErr) {
    throw new ApiError('Network error — is the server running?', 0, null);
  }

  // 204 / empty bodies are valid.
  let data = null;
  const text = await response.text();
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = null;
    }
  }

  if (!response.ok) {
    const message =
      (data && (data.error || data.detail)) || `Request failed (${response.status}).`;
    throw new ApiError(message, response.status, data);
  }

  return data;
}

export const api = {
  get: (path, opts) => request(path, { ...opts, method: 'GET' }),
  post: (path, body, opts) => request(path, { ...opts, method: 'POST', body }),
};

export default api;
